import { useMemo } from 'react';

import { useIntelligenceData } from '@/features/intelligence-dashboard/useIntelligenceData';

import { buildCustomerNameMap, detokenizeCustomerNames } from './detokenize';

/**
 * SCRUM-1022: returns a `(text) => text` de-tokenizer that swaps the NLQ
 * snapshot's opaque `CUST_<portfolioId>` tokens for real customer names, using
 * the already-cached `/api/customers` dataset (React Query key
 * `['intelligence-customers']`, staleTime: Infinity — shared with the dashboard
 * and campaigns, so it is usually already warm; fetched once if cold).
 *
 * Authorization is safe: every role that can reach Ask BankIQ
 * (Executive / BankAdmin / PlatformAdmin) also satisfies the `authenticated`
 * policy on `/api/customers`. If the data is not yet available the identity
 * function is returned (fail-open — tokens stay visible, never a blank).
 */
export function useDetokenizer(): (text: string) => string {
  const { data: customers } = useIntelligenceData();
  return useMemo(() => {
    if (!customers || customers.length === 0) {
      return (text: string) => text;
    }
    const map = buildCustomerNameMap(customers);
    return (text: string) => detokenizeCustomerNames(text, map);
  }, [customers]);
}
